import {
	Component,
	Directive,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	Output,
	booleanAttribute,
	forwardRef,
	ChangeDetectionStrategy,
} from "@angular/core"
import { Contactway, ValueName } from "@app/definitions/types"
import { ApiService } from "@app/services/api.service"
import { EventsService } from "@app/services/events.service"
import { LangService } from "@app/services/lang.service"
import { StoreService } from "@app/services/store.service"
import { LangValPipe } from "./lang-val.pipe"
import { ConfigValPipe } from "./config-val"

import { TitleLangDirective } from "./title-lang.directive"
import { flipYesNo } from "@app/helpers"
import { COMMON_IMPORTS } from "../common-imports"
import { ClsArrPipe } from "./cls-arr.pipe"
import { ModalService } from "@app/services/modal.service"
import { GenInputComponent } from "@app/gen-inputs/gen-input/gen-input.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { HomeMeetingComponent } from "@app/home-meeting/home-meeting.component"
import { DateFormatPipe } from "./date-format.pipe"
import { PatientComponent } from "@app/patient/patient.component"
import { NgClass } from "@angular/common"

@Component({
	selector: "[sorter]",
	imports: [forwardRef(() => Icon), ClsArrPipe],
	host: {
		"[class.pointer]": "isSortable",
		"[style.font-weight]": "'bold'",
		"[title]": "displayName",
	},
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		{{ displayName }}
		@if (isSortable) {
			<app-icon
				[icon]="sortField == fieldName && sortIsAsc ? 'sortUp' : 'sortDown'"
				[className]="[sortField === fieldName && 'green'] | clsArr"
			/>
		}
	`,
})
export class SortThComponent {
	@Input() displayName: string = ""
	@Input() fieldName: string = ""
	@Input() sortField: any = ""
	@Input() sortIsAsc: boolean = false
	@Input() isSortable: boolean = true
}

@Component({
	selector: "app-spin-or-content",
	imports: [forwardRef(() => Spin)],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (isSpin) {
			<app-spin style="margin:0" />
		} @else {
			<ng-content />
		}
	`,
})
export class SpinOrContent {
	@Input() isSpin: boolean = false
}

@Component({
	selector: "app-yes-no",
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div class="yes-no">
			@if (disabled) {
				<button
					class="btn-icon"
					[class.green]="isCheck"
					[class.unchecked]="!isCheck"
				>
					{{ isCheck ? "✔" : "✘" }}
				</button>
			} @else {
				<button
					class="btn-icon"
					[class.green]="isCheck"
					[class.unchecked]="!isCheck"
					(click)="onClick.emit(true); $event.stopPropagation()"
				>
					{{ isCheck ? "✔" : "✘" }}
				</button>
			}
		</div>
	`,
})
export class YesNo {
	@Output("onClick") onClick = new EventEmitter()
	@Input() isCheck: boolean = false
	@Input({ transform: booleanAttribute }) disabled: boolean = false
}

@Component({
	selector: "app-check",
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<input
			type="checkbox"
			class="check-icon"
			[disabled]="disabled"
			[checked]="isCheck"
			(change)="onClick.emit(true)"
			(click)="$event.stopPropagation()"
		/>
	`,
})
export class Check {
	@Output("onClick") onClick = new EventEmitter()
	@Input() isCheck: boolean = false
	@Input({ transform: booleanAttribute }) disabled: boolean = false
}

@Component({
	selector: "app-icon,[icon]",
	imports: [LangValPipe, ConfigValPipe, NgClass],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (isImg) {
			<img
				[src]="'img.' + icon | configVal"
				[alt]="titleLang | langVal"
				[class.pointer]="pointer"
				[ngClass]="className"
				style="height:100%;"
			/>
		} @else {
			<i
				[ngClass]="
					('icon.' + icon | configVal) + (className ? ' ' + className : '')
				"
				[class.pointer]="pointer"
				[attr.title]="(titleLang | langVal) || null"
			></i>
		}
	`,
})
export class Icon {
	@Input({ transform: booleanAttribute }) isImg: boolean = false
	@Input() icon: string = ""
	@Input() titleLang: string = ""
	@Input() className: string = ""
	@Input({ transform: booleanAttribute }) pointer: boolean = false
}

@Component({
	imports: [Icon],
	selector: "app-spin,[spin]",
	changeDetection: ChangeDetectionStrategy.Eager,
	template: ` <app-icon icon="spin" />`,
})
export class Spin {}

@Component({
	selector: "app-toast-download",
	imports: [COMMON_IMPORTS, LangValPipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: ` <div
		class="snackbar download"
		[class.doHide]="doHide"
		spinner
	>
		{{ "download_process" | langVal }}
	</div>`,
})
export class ToastDownloadComponent {
	doHide: boolean = true //is in show state flag
	constructor(private store: StoreService) {
		this.store.downloadSubject.subscribe(
			(isStart: boolean) => (this.doHide = !isStart)
		)
	}
}

@Component({
	selector: "app-test-display",
	imports: [COMMON_IMPORTS],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@for (testInner of test?.tests; track testInner) {
			<div>
				<h3>{{ testInner.name }}</h3>
				<app-test-display [test]="testInner" />
			</div>
		}
		@for (ques of test?.questions; track ques) {
			<div>
				<b style="display: block;">{{ ques.q }}</b>
				{{ ques.a }}
			</div>
		}
	`,
})
export class TestDisplayComponent {
	@Input() test: any
}

@Component({
	selector: "app-table-wrap",
	imports: [LangValPipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div [class.table-responsive]="isResponsive">
			<table class="table table-hover pad-block">
				@if (fields.length) {
					<thead>
						<tr>
							@for (field of fields; track field) {
								<th [style.width]="widths[field] ? widths[field] + '%' : null">
									{{ field | langVal }}
								</th>
							}
						</tr>
					</thead>
				}
				<tbody>
					<ng-content></ng-content>
				</tbody>
			</table>
		</div>
	`,
})
export class TableWrapComponent {
	fields: string[] = []
	@Input() fieldsStr: string = ""
	@Input() fieldsArr: string[] | null = null
	@Input() isStriped: boolean = true
	@Input({ transform: booleanAttribute }) isResponsive: boolean = true
	widths: any = {}

	ngOnInit() {
		this.fields = this.fieldsStr.split(",")
		if (this.fieldsArr) {
			this.fields = this.fieldsArr
		}
		this.fields.forEach((field, ind) => {
			if (field.includes(":")) {
				const r = field.split(":")
				this.fields[ind] = r[0]
				this.widths[r[0]] = r[1]
			}
		})
	}
}

@Component({
	imports: [COMMON_IMPORTS, LangValPipe],
	selector: "app-cancel-btn",
	changeDetection: ChangeDetectionStrategy.Eager,
	template: ` <button
		class="btn btn-default"
		titleLang="cancel"
		tabindex="-1"
	>
		{{ "cancel" | langVal }}
	</button>`,
})
export class CancelBtnComponent {}

@Component({
	imports: [Spin],
	selector: "[spinner]",
	changeDetection: ChangeDetectionStrategy.Eager,
	template: ` <ng-content />
		@if (spinner) {
			<app-spin />
		}`,
})
export class SpinnerComponent {
	@Input({ transform: booleanAttribute }) spinner: boolean = false
}

@Component({
	imports: [Icon],
	selector: "[preIcon]",
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `@if (preIcon) {
			<app-icon
				[icon]="preIcon"
				className="pre-icon"
				[isImg]="isImg"
			/>
		}
		<ng-content />`,
})
export class PreIconComponent {
	@Input() preIcon: string = ""
	@Input({ transform: booleanAttribute }) isImg: boolean = false
}
@Component({
	selector: "[postIcon]",
	imports: [Icon],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `<ng-content />
		@if (postIcon) {
			<app-icon
				[icon]="postIcon"
				className="post-icon"
			/>
		}`,
})
export class PostIconComponent {
	@Input() postIcon: string = ""
	@Input({ transform: booleanAttribute }) isImg: boolean = false
}

@Directive({
	selector: "[tabClick]",
})
export class TabClickDirective {
	constructor(private el: ElementRef) {}

	@HostListener("keydown.tab", ["$event"]) cl(event: Event) {
		event.preventDefault()
		this.el.nativeElement.click()
	}
}

@Component({
	selector: "app-contactway-add",
	imports: [COMMON_IMPORTS, GenInputComponent, LangValPipe, YesNo],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<label>{{ "contactway" | langVal }}</label>
		@for (contactway of contactways; track contactway) {
			<div class="d-flex gap-2">
				<div class="colstat colstat-big">
					<app-gen-input
						[actualObj]="contactway"
						[errors]="contactway.errors"
						fieldName="contactway"
						title_ph_lang="contactway_1"
						(keyupEvent)="keyupContactway.emit(contactway)"
						(changeEvent)="changeEventContactway.emit(contactway)"
					/>
					@if (contactway?.warning_status) {
						<span [innerHTML]="contactway.warning_status | langVal"></span>
					}
					@if (contactway?.resultObjs?.length) {
						@for (obj of contactway.resultObjs; track obj.id) {
							<div
								(click)="onIdClicked.emit(obj.id)"
								class="pointer"
							>
								{{ obj.id }} - {{ obj?.name }}
							</div>
						}
					}
				</div>
				<div class="colstat colstat-medium">
					<app-gen-input
						[actualObj]="contactway"
						fieldName="name"
						(keyupEvent)="fieldChange.emit()"
					>
					</app-gen-input>
				</div>
				<!--remember for merge-->
				<div class="colstat colstat-small">
					<app-gen-input
						[actualObj]="contactway"
						fieldName="description"
						title_ph_lang="contact_description"
						(keyupEvent)="fieldChange.emit()"
					>
					</app-gen-input>
					<!--remember for merge-->
				</div>
				<div class="d-flex justify-content-end gap-3 flex-grow-1 ">
					<div class="switch-btn switch-btn-subtle">
						<app-yes-no
							(onClick)="handleMainChange(contactway)"
							[isCheck]="contactway.main == 'yes'"
						/>
						<label (click)="handleMainChange(contactway)">
							{{ "main" | langVal }}
						</label>
					</div>

					<button
						class="btn btn-icon m-0 p-lg-0"
						(click)="removeContactway.emit(contactway.id)"
						icon="delete"
					></button>
				</div>

				<!--
						<div class="col-11 colstat colstat-big">
							<app-gen-input
								[actualObj]="contactway"
								fieldName="description"
								(keyupEvent)="fieldChange.emit()"
							>
							</app-gen-input>
						</div>
					-->

				<!--          <div class="col-12 col-lg-3">-->
				<!--            <app-gen-button-group [actualObj]="contactway" fieldName="contactway_identifier"-->
				<!--                                  optionsListName="contactway_identifier_opts"-->
				<!--                                  (changeEvent)="fieldChange.emit()">-->
				<!--            </app-gen-button-group>-->
				<!--          </div>-->
			</div>
		}
		<div class="text-end w-100">
			<button
				class="btn btn-icon btn-plus"
				(click)="addContactway.emit()"
				icon="plus"
				style="padding: 0 !important"
			></button>
		</div>
	`,
})
export class ContactwayAddComponent {
	@Input() contactways: Contactway[] = []
	@Output() fieldChange = new EventEmitter()
	@Output() addContactway = new EventEmitter()
	@Output() removeContactway = new EventEmitter()
	@Output() keyupContactway = new EventEmitter()
	@Output() changeEventContactway = new EventEmitter()
	@Output() onIdClicked = new EventEmitter()

	handleMainChange(contactway: Contactway) {
		contactway.main = flipYesNo(contactway.main)
		this.fieldChange.emit()
	}
}

@Component({
	imports: [COMMON_IMPORTS, LangValPipe, NgClass],
	selector: "app-btn-ext",
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<button
			class="btn"
			[ngClass]="'btn-' + btnClass"
			[titleLang]="title || text"
			[disabled]="disabled || isSpin"
			[spinner]="isSpin"
			[tabindex]="tabindex"
			tabClick
			(click)="clickEv.emit(true)"
		>
			@if (iconName && iconIsPre) {
				<app-icon [icon]="iconName" />
			}
			{{ text | langVal }}
			@if (iconName && !iconIsPre) {
				<app-icon [icon]="iconName" />
			}
		</button>
	`,
})
export class BtnExtComponent {
	@Output() clickEv = new EventEmitter()
	@Input() disabled: boolean = false
	@Input() isSpin: boolean = false
	@Input() text: string = ""
	@Input() title: string | null = null
	@Input() tabindex: string | null = null
	@Input() btnClass: string = "primary"
	@Input() iconName: string | null = null
	@Input({ transform: booleanAttribute }) iconIsPre: boolean = false
}

@Component({
	selector: "app-followup-modal",
	imports: [
		COMMON_IMPORTS,
		ModalWrapComponent,
		HomeMeetingComponent,
		DateFormatPipe,
		LangValPipe,
		PatientComponent,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `<div
		(click)="open()"
		[spinner]="isSpin"
	>
		<ng-content />
		@switch (modalProps?.name) {
			@case ("home-meeting") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					[title]="
						('edit_followup' | langVal) +
						' ' +
						$safeNavigationMigration(modalProps.user?.name) +
						' - ' +
						(modalProps.followup.date | dateFormat)
					"
					hasExit
				>
					<app-home-meeting
						(onClose)="modalProps = null"
						[followup]="modalProps.followup"
						isModalMode
					/>
				</app-modal-wrap>
			}
			@case ("patient") {
				<app-modal-wrap
					(onClose)="modalProps = null"
					[title]="
						modalProps.patient.name +
						' ' +
						($safeNavigationMigration(modalProps.followup?.date) | dateFormat)
					"
					modalWrapperClass="modal-1200"
				>
					<app-patient
						(onClose)="modalProps = null"
						[patient]="modalProps.patient"
						[followup]="modalProps.followup"
						isHomeMeetingOnly
					/>
				</app-modal-wrap>
			}
		}
	</div>`,
})
export class FollowupModalComponent {
	modalProps: any = null
	@Input() id: number = 0
	isSpin: boolean = false

	constructor(
		public store: StoreService,
		private apiService: ApiService,
		private modalService: ModalService
	) {}
	async open() {
		if (!this.id) {
			return
		}
		this.isSpin = true
		const res = await this.apiService.post("get_followup_by_id", {
			id: this.id,
		})
		if (res?.followup) {
			const followup = this.store.removeDecItem(res.followup)
			if (followup.patient_id) {
				const patient = await this.store.getPatient(followup.patient_id)
				this.modalProps = { name: "patient", followup, patient }
			} else {
				const user = this.store.searchSiteDataItem(followup.user_id, [
					"sub_users",
				])
				this.modalProps = { name: "home-meeting", followup, user }
			}
		}
		this.isSpin = false
	}
}

@Component({
	selector: "app-patient-modal",
	imports: [COMMON_IMPORTS, ModalWrapComponent, PatientComponent],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `<div
		(click)="open()"
		[spinner]="isSpin"
	>
		<ng-content />
		@if (modalProps) {
			<app-modal-wrap
				(onClose)="modalProps = null"
				[title]="modalProps.patient.name"
				modalWrapperClass="modal-1200"
			>
				<app-patient
					(onClose)="modalProps = null"
					[patient]="modalProps.patient"
					[openInTests]="openInTests"
				/>
			</app-modal-wrap>
		}
	</div>`,
})
export class PatientModalComponent {
	@Input() id: number = 0
	@Input({ transform: booleanAttribute }) openInTests: boolean = false
	isSpin: boolean = false
	modalProps: any = null

	constructor(public store: StoreService) {}
	async open() {
		if (!this.id) {
			return
		}
		this.isSpin = true
		const patient = await this.store.getPatient(this.id)
		this.isSpin = false
		this.modalProps = { patient }
	}
}

@Component({
	selector: "app-logo-gen",
	imports: [COMMON_IMPORTS],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div
			class="LOGO"
			title="My-cliniq"
			style="
				justify-content: flex-end;
				display: flex;"
		>
			<img
				src="//app2.my-cliniq.com/images/newdesign/main-logo.svg"
				alt="My-cliniq"
			/>
		</div>
	`,
})
export class LogoGenComponent {}

@Component({
	selector: "app-payment-wrap",
	imports: [COMMON_IMPORTS, LogoGenComponent],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div
			class="container-fluid"
			style="background-color: inherit"
		>
			<div style="line-height: 1.6">
				<app-logo-gen />
				<div>
					<div
						class="well well-lg"
						style="
							padding: 18px;
							border-top: 1px solid var(--light-grey);
						"
					>
						<div>
							<ng-content />
						</div>
					</div>
				</div>
				<div class="footer"></div>
			</div>
		</div>
	`,
})
export class PaymentWrapComponent {}

@Component({
	selector: "app-checkboxes-gen",
	imports: [COMMON_IMPORTS, LangValPipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div class="checkboxes d-grid gap-2">
			<div class="d-flex align-items-center gap-3">
				<input
					[checked]="allOp"
					(change)="flipAll()"
					type="checkbox"
				/>
				<span (click)="flipAll()">
					{{ "select_all_fields" | langVal }}
				</span>
			</div>
			<hr />
			@for (field of fields; track field.value) {
				<div
					class="d-flex align-items-center gap-3"
					(click)="handleChange(field.value, !obj[field.value])"
				>
					<input
						[checked]="obj[field.value]"
						type="checkbox"
					/>
					<span>
						{{ field.name | langVal }}
					</span>
				</div>
			}
		</div>
	`,
})
export class CheckboxesGenComponent {
	@Input() fields: ValueName[] = []
	@Input() title: string = ""
	@Input() obj: any = null
	@Output() onChange = new EventEmitter()

	allOp: boolean = true

	handleChange(field: any, newValue: boolean) {
		this.obj[field] = newValue
		this.onChange.emit(this.obj)
	}

	flipAll() {
		this.allOp = !this.allOp
		this.obj = this.fields.reduce(
			(prev, { value }) => ({ ...prev, [value]: this.allOp }),
			{}
		)
		this.onChange.emit(this.obj)
	}
}

@Component({
	selector: "app-btn-dropdown",
	imports: [COMMON_IMPORTS, LangValPipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		@if (isOpen) {
			<div class="btn-dropdown dropdown-list">
				@for (opt of options; track opt.id) {
					<div
						(click)="handleChoose(opt.id)"
						class="btn btn-primary"
					>
						{{ opt.value | langVal }}
						@if (opt?.icon) {
							<app-icon [icon]="opt.icon" />
						}
					</div>
				}
			</div>
		}
		<button
			class="btn btn-primary"
			[spinner]="isSpin"
			(click)="handleClick($event)"
		>
			<app-icon [icon]="iconName" />
		</button>
	`,
})
export class BtnDropdownComponent {
	@Input() isSpin: boolean = false
	@Input() iconName: string
	@Input() options: any[] = []
	@Output("onChoose") onChoose = new EventEmitter()

	isOpen: boolean = false

	constructor(public eventsService: EventsService) {}

	ngOnInit() {
		this.eventsService.docClickSubject.subscribe((val: any) => {
			if (val.src != this) {
				this.isOpen = false
			}
		})
	}
	handleChoose(id: any) {
		this.onChoose.emit(id)
		this.isOpen = false
	}
	handleClick(ev: any) {
		console.log("here")
		ev.stopImmediatePropagation()
		this.eventsService.onDocClick({ src: this })
		this.isOpen = true
	}
}
